import { ErrorOutline, InfoOutlined, OpenInNew, RefreshOutlined } from '@mui/icons-material'
import CloseIcon from '@mui/icons-material/Close'
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  IconButton,
  Link,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography
} from '@mui/material'
import dayjs from 'dayjs'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { Controller, FormProvider, useForm, useWatch } from 'react-hook-form'

import {
  CheckAssignmentIssue,
  CheckConflictSource,
  PreviewCheckAssignmentsQuery,
  usePreviewCheckAssignmentsLazyQuery
} from '../../../../generated'
import { ApprovedCheckAssignment } from '../../../../services'
import { AccountOption } from '../../../interfaceTypes/common'
import { ProcessPayableFormValues } from '../../../interfaceTypes/forms'
import { enumToLabel, formatCurrency } from '../../../utils'
import { payableStatusColors } from '../../../utils/billing'
import AccountDropdown from '../../common/AccountDropdown'
import { CustomController } from '../../common/CustomController'
import { CustomModal } from '../../common/CustomModal'

type CheckPlan = PreviewCheckAssignmentsQuery['previewCheckAssignments']
type CheckPlanRow = CheckPlan['rows'][number]

/** The check number is never printed on the check — it comes from the leaf in the printer — so the
 *  plan has to be re-fetched, not guessed client-side, whenever the inputs change. */
const RESCAN_DEBOUNCE_MS = 500

interface CheckModalProps {
  isOpen: boolean
  handleClose: () => void
  startPrinting: (
    data: ProcessPayableFormValues,
    startingCheckNo?: string,
    bankAccountId?: string,
    assignments?: ApprovedCheckAssignment[]
  ) => Promise<void>
  /** Voucher numbers of the payables the user ticked, in selection order. */
  vouchers: string[]
  /** Takes one voucher out of the selection so a blocked run can be fixed without leaving the modal. */
  onRemoveVoucher: (voucherNo: string) => void
  // Distinct bank accounts of the currently selected payables, used to pre-fill the dropdown.
  bankAccounts?: AccountOption[]
  // Check number to pre-fill (set when a single selected payable already has one assigned).
  defaultCheckNo?: string
}

/**
 * A payable with no number of its own, in a run with no starting number, is not an error — it is a
 * form that has not been filled in yet. Those rows are left plain and the modal asks for the number
 * instead of accusing the row.
 */
const isAwaitingStartNo = (row: CheckPlanRow) => row.issue === CheckAssignmentIssue.MissingNumber

const isBlocked = (row: CheckPlanRow) => row.issue !== CheckAssignmentIssue.None && !isAwaitingStartNo(row)

/**
 * A payable that has been printed before is not a problem — drawing the same check again is allowed.
 * It is worth saying out loud, though, because the operator may only have wanted to look at it, and
 * Preview Checks does that without consuming a leaf or touching the ledger.
 */
const ReprintNote = ({ printedAt }: { printedAt: string }) => (
  <Tooltip
    title={`Already printed on ${dayjs(printedAt).format('MM/DD/YYYY hh:mm A')}. Re-printing draws the same check again. If you only want to look at it, use Preview Checks instead — that marks nothing as Paid.`}
  >
    <Typography
      variant="caption"
      color="text.secondary"
      display="block"
      sx={{ mt: 0.25, cursor: 'help', textDecoration: 'underline dotted' }}
    >
      Reprint — printed {dayjs(printedAt).format('MM/DD/YYYY')}
    </Typography>
  </Tooltip>
)

const CheckModal = ({
  isOpen,
  handleClose,
  startPrinting,
  vouchers,
  onRemoveVoucher,
  bankAccounts = [],
  defaultCheckNo = ''
}: CheckModalProps) => {
  const methods = useForm<{ checkNo: string; bankAccount: AccountOption | null }>({
    defaultValues: {
      checkNo: '',
      bankAccount: null
    }
  })

  const {
    control,
    reset,
    setValue,
    formState: { errors }
  } = methods

  const [plan, setPlan] = useState<CheckPlan | null>(null)
  const [scanError, setScanError] = useState<string | null>(null)
  const [isPrinting, setIsPrinting] = useState(false)

  const checkNo = useWatch({ control, name: 'checkNo' })
  const bankAccount = useWatch({ control, name: 'bankAccount' })

  const hasMultipleBankAccounts = bankAccounts.length > 1

  const [previewCheckAssignments, { loading: scanning }] = usePreviewCheckAssignmentsLazyQuery({
    // The plan is a claim about rows other people can also be editing, so it must never be served
    // from the cache — a stale "all clear" is exactly the failure this modal exists to prevent.
    fetchPolicy: 'network-only',
    nextFetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
    onError: ({ message }) => {
      setPlan(null)
      setScanError(message)
    },
    onCompleted: ({ previewCheckAssignments: result }) => {
      setScanError(null)
      setPlan(result)
    }
  })

  const rescan = useCallback(() => {
    if (!vouchers.length || !bankAccount?.value) {
      setPlan(null)
      return
    }
    previewCheckAssignments({
      variables: {
        input: {
          vouchers,
          startingCheckNo: checkNo?.trim() || undefined,
          bankAccountId: bankAccount.value
        }
      }
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vouchers, checkNo, bankAccount?.value])

  // This modal stays mounted, so reset every time it opens — pre-fills the bank account from the
  // selected payables (only when they all share one account) and the check number when a single
  // selected payable already has one assigned; otherwise both start empty.
  useEffect(() => {
    if (isOpen) {
      setPlan(null)
      setScanError(null)
      setIsPrinting(false)
      reset({
        checkNo: defaultCheckNo ?? '',
        bankAccount: bankAccounts.length === 1 ? bankAccounts[0] : null
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen])

  // Debounced so typing a five-digit check number costs one scan, not five. `rescan` also changes
  // identity when a voucher is removed, which is what renumbers the shortened run.
  useEffect(() => {
    if (!isOpen) return
    const timer = setTimeout(rescan, RESCAN_DEBOUNCE_MS)
    return () => clearTimeout(timer)
  }, [isOpen, rescan])

  const blockedCount = useMemo(() => plan?.rows.filter(isBlocked).length ?? 0, [plan])
  const needsStartNo = useMemo(() => !!plan?.rows.some(isAwaitingStartNo), [plan])

  const canPrint = !!plan?.canPrint && !scanning && !isPrinting && !!bankAccount?.value

  const onSubmit = async (data: { checkNo: string; bankAccount: AccountOption | null }) => {
    if (!canPrint || !plan) return
    const { checkNo: startingCheckNo, bankAccount: account } = data
    setIsPrinting(true)
    handleClose()
    // The pairings exactly as reviewed. The API rebuilds the same plan and refuses the run if it no
    // longer matches, so nothing can be written that was not on screen.
    await startPrinting(
      { action: 'print-check' },
      startingCheckNo,
      account?.value ?? undefined,
      plan.rows.map(row => ({ voucherNo: row.voucherNo, checkNo: row.checkNo }))
    )
  }

  return (
    <CustomModal isOpen={isOpen} handleClose={handleClose} title="Set Starting Check Number" maxWidth={'lg'}>
      <FormProvider {...methods}>
        <form onSubmit={methods.handleSubmit(onSubmit)}>
          {hasMultipleBankAccounts && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              Your selected payables are associated with different bank accounts. Are you sure you want to print them on
              the same bank's check leaves?
            </Alert>
          )}

          <Box display="flex" gap={2} alignItems="flex-start" flexWrap="wrap">
            <FormControl variant="outlined" sx={{ flex: '1 1 320px', minWidth: 260 }}>
              <Controller
                name="bankAccount"
                control={control}
                rules={{ required: 'Bank account is required' }}
                render={({ field }) => (
                  <AccountDropdown
                    value={field.value}
                    onChange={option => field.onChange(option)}
                    searchFilters={{ isBankAccount: true }}
                    label="Bank Account"
                    sx={{ mt: 1 }}
                  />
                )}
              />
              {errors.bankAccount && (
                <Typography variant="caption" color="error" sx={{ mt: 0.5 }}>
                  {(errors.bankAccount as any).message}
                </Typography>
              )}
            </FormControl>

            <Box sx={{ flex: '0 1 220px', minWidth: 180 }}>
              <CustomController controllerLabel="Starting Check No" controllerName="checkNo" />
            </Box>

            <Button
              variant="outlined"
              onClick={rescan}
              disabled={scanning || !bankAccount?.value}
              startIcon={scanning ? <CircularProgress size={16} /> : <RefreshOutlined />}
              sx={{ mt: 1, height: 56 }}
            >
              Rescan
            </Button>
          </Box>

          <CheckPlanSummary
            plan={plan}
            scanning={scanning}
            scanError={scanError}
            blockedCount={blockedCount}
            needsStartNo={needsStartNo}
            hasBank={!!bankAccount?.value}
            onUseNextAvailable={next => setValue('checkNo', next, { shouldDirty: true })}
          />

          <CheckPlanTable plan={plan} scanning={scanning} onRemoveVoucher={onRemoveVoucher} />

          <Box display={'flex'} gap={1} my={2}>
            <InfoOutlined />
            <Box>
              <Typography variant="subtitle1">
                Checks print on pre-numbered stock, one payable per leaf, so each payable above is paired with the exact
                check it will be printed on.
                {plan?.firstCheckNo ? (
                  <>
                    {' '}
                    Load the printer with check <b>{plan.firstCheckNo}</b> first.
                  </>
                ) : null}
              </Typography>
              <Typography variant="subtitle1" sx={{ mt: 1, fontWeight: 600 }}>
                Note: Printing these checks will mark the selected payables as <b>Paid</b>. This will post the
                corresponding journal entries, payments and expense entries, and (for trust payables) withdraw from the
                client's trust balance. This action cannot be undone except by voiding the payable.
              </Typography>
            </Box>
          </Box>

          <Box display={'flex'} justifyContent={'end'} alignItems="center" gap={2} mt={2}>
            {!canPrint && !scanning && blockedCount > 0 && (
              <Typography variant="caption" color="text.secondary">
                Resolve or remove {blockedCount} flagged {blockedCount === 1 ? 'row' : 'rows'} to enable printing.
              </Typography>
            )}
            <Tooltip title={canPrint ? '' : 'Every payable needs a free check number before printing.'}>
              <span>
                <Button type="submit" variant="contained" disabled={!canPrint}>
                  Start Printing
                </Button>
              </span>
            </Tooltip>
          </Box>
        </form>
      </FormProvider>
    </CustomModal>
  )
}

interface CheckPlanSummaryProps {
  plan: CheckPlan | null
  scanning: boolean
  scanError: string | null
  blockedCount: number
  needsStartNo: boolean
  hasBank: boolean
  onUseNextAvailable: (next: string) => void
}

const CheckPlanSummary = ({
  plan,
  scanning,
  scanError,
  blockedCount,
  needsStartNo,
  hasBank,
  onUseNextAvailable
}: CheckPlanSummaryProps) => {
  if (scanError) {
    return (
      <Alert severity="error" sx={{ mt: 2 }}>
        {scanError}
      </Alert>
    )
  }

  if (!hasBank) {
    return (
      <Alert severity="info" sx={{ mt: 2 }}>
        Select the bank account whose check stock is loaded in the printer.
      </Alert>
    )
  }

  if (!plan || scanning) return <Box sx={{ mt: 2 }} />

  if (plan.canPrint) {
    return (
      <Alert severity="success" sx={{ mt: 2 }}>
        {plan.rows.length} {plan.rows.length === 1 ? 'check' : 'checks'} ready on {plan.firstCheckNo}
        {plan.rows.length > 1 ? ` – ${plan.lastCheckNo}` : ''}. No number in this range is in use.
      </Alert>
    )
  }

  // Asked for, not complained about: nothing is wrong with these rows yet, they just have no number.
  if (needsStartNo && blockedCount === 0) {
    return (
      <Alert severity="info" sx={{ mt: 2 }}>
        Enter the number printed on the first check leaf in the printer, and each payable below will be paired with the
        check it lands on.
      </Alert>
    )
  }

  return (
    <Alert
      severity="error"
      sx={{ mt: 2 }}
      action={
        plan.nextAvailableCheckNo ? (
          <Button color="inherit" size="small" onClick={() => onUseNextAvailable(plan.nextAvailableCheckNo as string)}>
            Use {plan.nextAvailableCheckNo}
          </Button>
        ) : undefined
      }
    >
      {plan.suggestion ??
        `${blockedCount} ${blockedCount === 1 ? 'payable' : 'payables'} cannot be printed on the numbers shown.`}
    </Alert>
  )
}

interface CheckPlanTableProps {
  plan: CheckPlan | null
  scanning: boolean
  onRemoveVoucher: (voucherNo: string) => void
}

const CheckPlanTable = ({ plan, scanning, onRemoveVoucher }: CheckPlanTableProps) => {
  if (scanning && !plan) {
    return (
      <Box display="flex" justifyContent="center" py={4}>
        <CircularProgress size={28} />
      </Box>
    )
  }

  if (!plan) return null

  return (
    <TableContainer component={Paper} variant="outlined" sx={{ mt: 2, maxHeight: 380, opacity: scanning ? 0.6 : 1 }}>
      <Table size="small" stickyHeader>
        <TableHead>
          <TableRow>
            <TableCell width={48}>#</TableCell>
            <TableCell>Payable</TableCell>
            <TableCell width={90}>Status</TableCell>
            <TableCell align="right" width={110}>
              Amount
            </TableCell>
            <TableCell width={110}>Check No</TableCell>
            <TableCell width={300}>Issue</TableCell>
            <TableCell width={56} align="center" />
          </TableRow>
        </TableHead>

        <TableBody>
          {plan.rows.map((row, index) => (
            <CheckPlanTableRow
              key={row.voucherNo}
              row={row}
              index={index}
              // The last row cannot be removed: an empty run has nothing to show or print.
              canRemove={plan.rows.length > 1}
              onRemove={() => onRemoveVoucher(row.voucherNo)}
            />
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  )
}

interface CheckPlanTableRowProps {
  row: CheckPlanRow
  index: number
  canRemove: boolean
  onRemove: () => void
}

const CheckPlanTableRow = ({ row, index, canRemove, onRemove }: CheckPlanTableRowProps) => {
  const blocked = isBlocked(row)

  return (
    <TableRow
      sx={
        blocked
          ? {
              // A marker rather than a wash: the row still has to be readable, and the numbers in it
              // are the point.
              '& td:first-of-type': { boxShadow: theme => `inset 3px 0 0 ${theme.palette.error.main}` },
              '& td': { borderBottomColor: 'error.light' }
            }
          : undefined
      }
    >
      <TableCell>{index + 1}</TableCell>

      <TableCell>
        <Typography variant="body2" fontWeight={600}>
          {row.voucherNo}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {row.vendorName || '—'}
        </Typography>
      </TableCell>

      {/* A voided or already-paid payable has no business on fresh check stock; the status is the
          only thing on this row that says so. */}
      <TableCell>
        {row.status ? (
          <Chip
            size="small"
            label={enumToLabel(row.status)}
            sx={{
              color: payableStatusColors[row.status],
              border: `1px solid ${payableStatusColors[row.status]}50`,
              backgroundColor: 'transparent'
            }}
          />
        ) : (
          <Typography variant="caption" color="text.secondary">
            —
          </Typography>
        )}
      </TableCell>

      <TableCell align="right">{formatCurrency(row.amount)}</TableCell>

      <TableCell>
        <Typography variant="body2" fontWeight={600} color={blocked ? 'error.main' : undefined}>
          {row.checkNo ?? '—'}
        </Typography>
        {/* Worth calling out: a pre-assigned number is the second way a run drifts off the paper. */}
        {row.existingCheckNo && row.existingCheckNo !== row.checkNo && (
          <Typography variant="caption" color="text.secondary">
            was {row.existingCheckNo}
          </Typography>
        )}
      </TableCell>

      <TableCell>
        <Box>
          {blocked ? (
            <Box display="flex" alignItems="flex-start" gap={0.75}>
              <ErrorOutline color="error" fontSize="small" sx={{ mt: '2px', flexShrink: 0 }} />
              <Box>
                <Typography variant="caption" color="error.main" display="block">
                  {row.issueMessage}
                </Typography>
                {row.conflict?.source === CheckConflictSource.Payable && row.conflict.voucherNo && (
                  <Link
                    href={`/accounts/payable/${row.conflict.voucherNo}`}
                    target="_blank"
                    rel="noopener"
                    variant="caption"
                    sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.25, whiteSpace: 'nowrap' }}
                  >
                    Open {row.conflict.voucherNo}
                    <OpenInNew fontSize="inherit" />
                  </Link>
                )}
              </Box>
            </Box>
          ) : row.checkNo ? (
            <Chip size="small" color="success" variant="outlined" label="Ready" />
          ) : (
            <Typography variant="caption" color="text.secondary">
              Awaiting starting check number
            </Typography>
          )}

          {row.printedAt && <ReprintNote printedAt={row.printedAt} />}
        </Box>
      </TableCell>

      <TableCell align="center">
        <Tooltip title={canRemove ? 'Remove from this print run' : 'A print run needs at least one payable'}>
          <span>
            <IconButton size="small" onClick={onRemove} disabled={!canRemove} aria-label={`Remove ${row.voucherNo}`}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      </TableCell>
    </TableRow>
  )
}

export { CheckModal }
